import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { useEffect, useState } from 'react';
import { useRequestCache } from '../../../shared/hooks/use-request-cache';
import { AutosuggestSearchParams } from '../services/SearchService';
import { useSearchService } from './serviceHooks';
import { SearchSuggestions } from '../types/mapped';
import { Gender } from '../../../shared/services/gender-cookie-service/gender-cookie-service.types';

export type UseSearchSuggestionsParams = AutosuggestSearchParams & {
    request: IRequestContext;
    telemetry: ITelemetry;
};

export const useSearchSuggestions = (params: UseSearchSuggestionsParams) => {
    const {
        request,
        telemetry,
        numberOfCategoryResults,
        numberOfKeywordResults,
        numberOfProductResults,
        query,
        gender,
        shouldFetchCategories,
        shouldFetchProducts,
        shouldFetchSearchTermPredictions
    } = params;
    const [suggestions, setSuggestions] = useState<SearchSuggestions>();
    const [isLoading, setIsLoading] = useState(false);

    const relewiseSearchService = useSearchService(request, telemetry);

    const { getCached, setCached } = useRequestCache<Gender | undefined, SearchSuggestions>([query]);

    useEffect(
        function loadSuggestions() {
            if (!query) {
                setSuggestions(undefined);
                setIsLoading(false);
                return;
            }

            const cachedSuggestions = getCached(gender);
            if (cachedSuggestions) {
                setSuggestions(cachedSuggestions);
                setIsLoading(false);
                return;
            }

            const abortController = new AbortController();
            setIsLoading(true);

            const fetchSuggestions = async (): Promise<void> => {
                const result = await relewiseSearchService.autosuggestedSearch(
                    {
                        numberOfCategoryResults,
                        numberOfKeywordResults,
                        numberOfProductResults,
                        query,
                        gender,
                        shouldFetchCategories,
                        shouldFetchProducts,
                        shouldFetchSearchTermPredictions
                    },
                    abortController.signal
                );

                if (!abortController.signal.aborted) {
                    if (result && gender) {
                        setCached(gender, result);
                    }
                    setSuggestions(result);
                    setIsLoading(false);
                }
            };

            fetchSuggestions();

            return () => {
                abortController.abort();
            };
        },
        [
            numberOfCategoryResults,
            numberOfKeywordResults,
            numberOfProductResults,
            query,
            gender,
            shouldFetchCategories,
            shouldFetchProducts,
            shouldFetchSearchTermPredictions,
            relewiseSearchService,
            getCached,
            setCached
        ]
    );

    return { suggestions, isLoading };
};
